
import React, { createContext, useContext, useEffect, useState } from 'react';
import { db, auth } from '../lib/firebase';
import { Loader2, AlertTriangle } from 'lucide-react';
import { TenantSubscription, SaaSPlan, SaaSFeatureKey } from '../types/saas';
import { useParams, useLocation } from 'react-router-dom';
import { DOMAIN_CONFIG, IS_LOCALHOST, isSaaSPlatform } from '../config/domainConfig';

interface Tenant {
    id: string;
    name: string;
    domains: string[]; 
    customDomain?: string;
    subdomain?: string;
    ownerId: string;
    subscriptionStatus: 'ACTIVE' | 'SUSPENDED'; 
    subscription?: TenantSubscription; 
    config: any; 
    timezone?: string;
    isRoot?: boolean; 
    industry?: string;
    logoUrl?: string;
    phone?: string;
}

interface TenantContextType {
    tenant: Tenant | null;
    currentPlan: SaaSPlan | null;
    loading: boolean;
    error: string | null;
    isPlatformAdmin: boolean;
    hasFeature: (feature: SaaSFeatureKey) => boolean;
}

const TenantContext = createContext<TenantContextType>({
    tenant: null,
    currentPlan: null,
    loading: true,
    error: null,
    isPlatformAdmin: false,
    hasFeature: () => false
});

export const useTenant = () => useContext(TenantContext);

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const params = useParams();
    const location = useLocation();
    const hostname = window.location.hostname;
    
    // CACHE KEY: Specific to the current domain/path to prevent cross-contamination
    const CACHE_KEY = `janoobi_tenant_${hostname}_${params.tenantSlug || 'root'}`;

    // 1. Initialize from Cache (Instant Load)
    const [tenant, setTenant] = useState<Tenant | null>(() => {
        try {
            const cached = sessionStorage.getItem(CACHE_KEY);
            return cached ? JSON.parse(cached) : null;
        } catch { return null; }
    });

    const [currentPlan, setCurrentPlan] = useState<SaaSPlan | null>(null);
    
    // If we have cached data, we are not "loading" visually, but we are "fetching" in background
    const [loading, setLoading] = useState(!tenant);
    const [error, setError] = useState<string | null>(null);
    
    const isHardcodedAdmin = isSaaSPlatform(hostname);

    useEffect(() => {
        let isMounted = true;
        
        const resolveTenant = async () => {
            // Only set loading true if we don't have a cache to show
            if (!tenant) setLoading(true);
            setError(null);

            let resolvedTenant: Tenant | null = null;
            let tenantId = '';

            // 1. PATH-BASED TENANT (/t/slug)
            let pathSlug = params.tenantSlug;
            if (!pathSlug) {
                const match = location.pathname.match(/^\/t\/([^/]+)/);
                if (match) pathSlug = match[1];
            }

            if (pathSlug) {
                try {
                    const snap = await db.collection('tenants').where('subdomain', '==', pathSlug).limit(1).get();
                    if (!snap.empty) {
                        resolvedTenant = snap.docs[0].data() as Tenant;
                        tenantId = snap.docs[0].id;
                    } else {
                         if (isMounted) setError('Workspace not found.');
                         if (isMounted) setLoading(false);
                         return;
                    }
                } catch (e) { console.error(e); }
            }
            
            // 2. DOMAIN-BASED TENANT (subdomain.app.com or custom.com)
            const isRoot = hostname === DOMAIN_CONFIG.SAAS_ROOT || hostname === `www.${DOMAIN_CONFIG.SAAS_ROOT}` || IS_LOCALHOST;

            if (!resolvedTenant && !isRoot && !isHardcodedAdmin) {
                 try {
                     let queryDomain = hostname;
                     let snapshot = await db.collection('tenants').where('domains', 'array-contains', queryDomain).limit(1).get();
                     
                     if (snapshot.empty) {
                         snapshot = await db.collection('tenants').where('customDomain', '==', queryDomain).limit(1).get();
                     }

                     if (!snapshot.empty) {
                         resolvedTenant = snapshot.docs[0].data() as Tenant;
                         tenantId = snapshot.docs[0].id;
                     } 
                } catch (e) { console.error(e); }
            }

            // 3. ROOT DOMAIN / DEFAULT TENANT
            if (!resolvedTenant) {
                try {
                    let ownerIdToUse = '';
                    
                    try {
                        const platformSettings = await db.collection('settings').doc('platform').get();
                        if (platformSettings.exists && platformSettings.data()?.defaultStudioOwnerId) {
                            ownerIdToUse = platformSettings.data()?.defaultStudioOwnerId;
                        }
                    } catch(e) {}

                    if (!ownerIdToUse) {
                        const superSnap = await db.collection('users').where('role', '==', 'SUPER_ADMIN').limit(1).get();
                        if (!superSnap.empty) ownerIdToUse = superSnap.docs[0].id;
                    }

                    if (ownerIdToUse) {
                        const ownerSnap = await db.collection('users').doc(ownerIdToUse).get();
                        const ownerData = ownerSnap.data();

                        if (ownerData) {
                            resolvedTenant = {
                                id: 'root_platform',
                                name: ownerData.invoiceDetails?.businessName || 'Main Studio',
                                domains: [hostname],
                                ownerId: ownerIdToUse,
                                subscriptionStatus: 'ACTIVE',
                                subdomain: 'root',
                                isRoot: true,
                                config: { terminology: { studio: 'Studio', artist: 'Artist', booking: 'Session' } }
                            };
                            
                            // Check for custom branding override
                            const studioSnap = await db.collection('studios').doc(ownerIdToUse).get();
                            if (studioSnap.exists) {
                                const sData = studioSnap.data();
                                resolvedTenant.name = sData?.name || resolvedTenant.name;
                            }
                            tenantId = 'root_platform';
                        }
                    }
                } catch (e) { console.error("Root Tenant Resolution Failed", e); }
            }

            if (isMounted) {
                if (resolvedTenant) {
                    if (resolvedTenant.subscriptionStatus === 'SUSPENDED') {
                        setError('This account has been suspended.');
                    } else {
                        const finalTenant = { ...resolvedTenant, id: tenantId };
                        // Compare with cache to avoid unnecessary re-renders
                        if (JSON.stringify(finalTenant) !== JSON.stringify(tenant)) {
                            setTenant(finalTenant);
                            sessionStorage.setItem(CACHE_KEY, JSON.stringify(finalTenant));
                        }
                    }
                } else {
                    setTenant(null);
                    sessionStorage.removeItem(CACHE_KEY);
                }
                setLoading(false);
            }
        };

        resolveTenant();

        return () => { isMounted = false; };
    }, [hostname, location.pathname, params.tenantSlug]);

    const hasFeature = (feature: SaaSFeatureKey): boolean => {
        if (tenant?.isRoot) return true;
        if (!currentPlan) return false;
        return currentPlan.features.includes(feature);
    };

    const authUser = auth.currentUser;
    const isPlatformAdmin = authUser?.uid === tenant?.ownerId || false;

    return (
        <TenantContext.Provider value={{ tenant, currentPlan, loading, error, isPlatformAdmin, hasFeature }}>
            {children}
        </TenantContext.Provider>
    );
};
